import React, { useContext, useEffect, useRef, useState } from "react";
import { useStudentNotifContext } from "../../../components/gcstudent/studenttopbar/StudentTopbar";
import "../../../assets/css/gcstudent/studentnotifications/StudentNotificationsPage.css";
import AppContext from "../../../context/AppContext";
import axios from "axios";
import Swal from "sweetalert2";
import Pagination from "../../../components/general/pagination/Pagination";
import Loader from "../../../components/general/loader/Loader";

/* ─── Notification Icon ─── */
const NotifIcon = ({ type, size = "md" }) => {
  const colorMap = {
    overdue: "gcStudentNP-icon--danger",
    fine:    "gcStudentNP-icon--danger",
    book:    "gcStudentNP-icon--success",
    review:  "gcStudentNP-icon--info",
    dispute: "gcStudentNP-icon--warning",
  };
  const symbols = { overdue: "!", fine: "₨", book: "✦", review: "★", dispute: "⚠" };
  return (
    <span className={`gcStudentNP-icon gcStudentNP-icon--${size} ${colorMap[type] || colorMap.review}`} aria-hidden="true">
      {symbols[type] || "●"}
    </span>
  );
};

/* ─── Main StudentNotificationsPage ─── */
const ITEMS_PER_PAGE = 10; // yahan se page size control hoti hai

const StudentNotificationsPage = () => {
  const { baseUrl, loggedInUser, authToken } = useContext(AppContext);

  const ctx = useStudentNotifContext();
  const [localNotifications, setLocalNotifications] = useState([]);

  const notifications    = ctx ? ctx.notifications    : localNotifications;
  const setNotifications = ctx ? ctx.setNotifications : setLocalNotifications;

  const [loading, setLoading]         = useState(false); // initial API fetch loading
  const [viewLoading, setViewLoading] = useState(false); // page switch loading (sirf visible notifications k liye)
  const [currentPage, setCurrentPage] = useState(1);      // pagination state

  // Pehli render (initial mount) par view-loading trigger na ho, is liye ref se track kar rahe hain
  const isFirstRender = useRef(true);

  const unreadCount = notifications?.filter((n) => n.status === "unread").length;

  const selectNotif = (n) => {
    setNotifications((prev) =>
      prev.map((item) => (item.id === n.id ? { ...item, status: "read" } : item))
    );
  };

  /* ── Fetch all notifications ── */
  const fetchAllNotifications = () => {
    if (!loggedInUser?.id) return;

    setLoading(true);

    const formData = new FormData();
    formData.append("lbstudent_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/student/notification/fetchAllNotifications`, formData, {
        headers: {
          "Content-Type": "multipart/formData",
          "Authorization": `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        if (res.data.success === true) {
          setLocalNotifications(res.data.notifications);
        } else {
          setLocalNotifications([]);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* ── Mark all as read ── */
  const markAllAsRead = () => {
    if (!loggedInUser?.id) return;

    const formData = new FormData();
    formData.append("lbstudent_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/student/notification/markAllAsRead`, formData, {
        headers: {
          "Content-Type": "multipart/formData",
          "Authorization": `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        if (res.data.success === true) {
          Swal.fire({
            title: 'Wow',
            icon: 'success',
            text: 'All Notifcations Marked As Read',
            confirmButtonText: 'Okay'
          });
          fetchAllNotifications();
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  /* ── Clear all notifications ── */
  const clearAllNotifications = () => {
    if (!loggedInUser?.id) return;

    Swal.fire({
      title: 'Clear All Notifications?',
      text: 'This will permanently delete all your notifications.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, clear all',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("lbstudent_id", loggedInUser.id);

        axios
          .post(`${baseUrl}/student/notification/deleteAllNotifications`, formData, {
            headers: {
              "Content-Type": "multipart/formData",
              "Authorization": `Bearer ${authToken}`,
            },
          })
          .then((res) => {
            if (res.data.success === true) {
              setLocalNotifications([]);
            }
          })
          .catch((err) => console.log(err));
      }
    });
  };

  useEffect(() => {
    if (loggedInUser?.id) {
      fetchAllNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser]);

  /* ── Pagination slice (sirf current page ki notifications list mein jayengi) ── */
  const paginatedNotifications = notifications.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  /* ── Agar list chhoti ho jaye (clear/delete) to current page ko valid range mein clamp ker do ── */
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(notifications.length / ITEMS_PER_PAGE));
    if (currentPage > maxPage) setCurrentPage(maxPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications.length]);

  /* ── Sirf visible notifications ki loading dikhayein — jab pagination
     se dusre page par jayen (e.g. page 1 k 10 notifications dikhane
     k baad page 2 pe jayen tu sirf 11-20 wali notifications ki loading
     chale). Poori list ek sath "pop" nahi hogi. ── */
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setViewLoading(true);
    const timer = setTimeout(() => setViewLoading(false), 400);
    return () => clearTimeout(timer);
  }, [currentPage]);

  return (
    <div className="gcStudentNP-root">

      {/* Page Header */}
      <div className="gcStudentNP-page-head">
        <div>
          <h1 className="gcStudentNP-page-title">Notifications</h1>
          <p className="gcStudentNP-page-sub">Stay updated with your books, fines and library activity.</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {notifications.length > 0 && (
            <button
              className="gcStudentNP-markall-btn" onClick={markAllAsRead} >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M2 7L5.5 10.5L12 3.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Mark All as Read
            </button>
          )}
          {notifications.length > 0 && (
            <button
              className="gcStudentNP-markall-btn"
              style={{ color: "#d33", borderColor: "#d33" }}
              onClick={clearAllNotifications}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M2 3.5H12M5 3.5V2.5H9V3.5M4 3.5V11.5H10V3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* ── Initial Loading (poora page pehli baar load ho raha hai) ── */}
      {loading && <Loader message="Notifications loading, Please wait..." />}

      {/* Layout */}
      {!loading && (
        <div className="gcStudentNP-layout">

          {/* List */}
          <div className="gcStudentNP-list-col">
            <div className="gcStudentNP-section-head">
              <span className="gcStudentNP-section-head__dot" aria-hidden="true" />
              <h2 className="gcStudentNP-section-head__title">Recent Notifications</h2>
              {unreadCount > 0 && (
                <span className="gcStudentNP-section-head__badge">{unreadCount}</span>
              )}
            </div>

            {viewLoading ? (
              /* ── Page switch loading — sirf is section ki, baaki page affect nahi hota ── */
              <Loader message="Loading notifications..." />
            ) : (
              <ul className="gcStudentNP-list" role="list">
                {paginatedNotifications.map((n, idx) => (
                  <li
                    key={n.id}
                    className={[
                      "gcStudentNP-row",
                      n.status === "unread" ? "gcStudentNP-row--unread" : "",
                    ].filter(Boolean).join(" ")}
                    style={{ animationDelay: `${idx * 0.055}s` }}
                    onClick={() => selectNotif(n)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && selectNotif(n)}
                  >
                    <NotifIcon type={n.type} />
                    <div className="gcStudentNP-row__body">
                      <div className="gcStudentNP-row__top">
                        <span className="gcStudentNP-row__title">
                          {n.title}
                          {n.status === "unread" && <span className="gcStudentNP-row__dot" aria-label="unread" />}
                        </span>
                      </div>
                      <p className="gcStudentNP-row__summary">{n.subtitle}</p>
                    </div>
                    {n.status === "read" && (
                      <span className="gcStudentNP-row__read-tick" aria-label="Read">
                        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                          <path d="M2 6.5L5 9.5L11 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    )}
                  </li>
                ))}

                {notifications.length === 0 && (
                  <li className="gcStudentNP-empty">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                      <path d="M20 5a11 11 0 0111 11v6l3 4H6l3-4v-6A11 11 0 0120 5zM17 35a3 3 0 006 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                    </svg>
                    <span>No notifications yet</span>
                  </li>
                )}
              </ul>
            )}

            {/* ── Pagination (view-loading k dauran bhi visible rehti hai) ── */}
            <Pagination
              currentPage={currentPage}
              totalItems={notifications.length}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setCurrentPage}
            />
          </div>

        </div>
      )}
    </div>
  );
};

export default StudentNotificationsPage;